<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-select
              ref="areaSelector"
              v-model="selectedArea"
              :loading="isLoading"
              :placeholder="$t('message.please_select') + $t('attributes.area')"
              @input="onSelectArea"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in areas"
                :value="option"
                :key="option.code"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-select
              ref="countrySelector"
              v-model="selectedCountry"
              :loading="isLoading"
              :placeholder="
                $t('message.please_select') + $t('attributes.country')
              "
              @input="onSelectCountry"
            >
              <option value="">
                {{ $t('attributes.all_area') }}
              </option>
              <option
                v-for="option in countries"
                :value="option"
                :key="option.id"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-select
              ref="regionSelector"
              v-model="selectedRegion"
              :loading="isLoading"
              :placeholder="
                $t('message.please_select') + $t('attributes.region')
              "
              @input="onSelectRegion"
            >
              <option value="">全部地區</option>
              <option
                v-for="option in regions"
                :value="option"
                :key="option.id"
              >
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="searchName"
              :placeholder="$t('attributes.search') + $t('attributes.name')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.city') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">{{ props.row.id }}</p>
      </b-table-column>

      <b-table-column
        field="area_name"
        :label="$t('attributes.area_name')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.area_name }}
      </b-table-column>

      <b-table-column
        field="country_name"
        :label="$t('attributes.country_name')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.country_name }}
      </b-table-column>

      <b-table-column
        field="region_name"
        :label="$t('attributes.region_name')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.region_name }}
      </b-table-column>

      <b-table-column
        field="code"
        :label="$t('attributes.city') + $t('attributes.code')"
        sortable
        v-slot="props"
      >
        {{ props.row.code }}
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.city_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.name }}
      </b-table-column>

      <b-table-column
        field="english_name"
        :label="$t('attributes.english_name')"
        sortable
        v-slot="props"
      >
        {{ props.row.english_name }}
      </b-table-column>

      <b-table-column :width="80" label="功能" centered v-slot="props">
        <div class="buttons">
          <b-button type="is-primary" @click.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isCreateModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <city-create-form
        :city="selectedItem"
        :is-sending="sending"
        @send="onSend"
      />
    </b-modal>

    <b-modal
      :active.sync="isUpdateModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <city-update-form
        :city="selectedItem"
        :is-sending="sending"
        @send="onSend"
      />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { AREA_API, COUNTRIES_API, CITIES_API, REGIONS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import CityCreateForm from './CityCreateForm'
import CityUpdateForm from './CityUpdateForm'

export default {
  name: 'CityList',
  components: { CityCreateForm, CityUpdateForm },

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      searchName: this.query.search || '',
      sortField: this.query.sort || 'id',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      loading: false,
      areas: [],
      selectedArea: null,
      selectedAreaCode: this.query.ac || null,

      countries: [],
      selectedCountry: null,
      selectedCountryId: this.query.ci ? parseInt(this.query.ci) : null,

      regions: [],
      selectedRegion: null,
      selectedRegionId: this.query.ri ? parseInt(this.query.ri) : null,

      isCreateModalActive: false,
      isUpdateModalActive: false,
      sending: false
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems || this.loading
    }
  },
  mounted() {
    this.loadDate()
  },
  methods: {
    async loadDate() {
      this.loading = true
      await this.loadAreas()
      this.loading = false

      this.$refs.areaSelector.focus()
    },
    async loadAreas() {
      try {
        const res = await this.axios.get(AREA_API)
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.selectedArea = null

        if (this.selectedAreaCode) {
          this.selectedArea = this.areas.find(
            a => a.code === this.selectedAreaCode
          )
          this.selectedAreaCode = this.selectedArea && this.selectedArea.code
        }

        if (this.selectedArea) {
          await this.loadCountries()
        }
      } catch (e) {
        this.areas = []
        this.selectedArea = null
        this.selectedAreaCode = null

        this.countries = []
        this.selectedCountry = null
        this.selectedCountryId = null

        notifyService.fetch_error('area', e)
      }
    },
    async loadCountries() {
      const api = `${AREA_API}/${this.selectedAreaCode}/countries`

      try {
        const res = await this.axios.get(api)
        this.countries = res.data.data
        this.selectedCountry = null

        if (this.selectedCountryId) {
          this.selectedCountry = this.countries.find(
            a => a.id === this.selectedCountryId
          )
          this.selectedCountryId =
            this.selectedCountry && this.selectedCountry.id
        }

        if (this.selectedCountry) {
          await this.loadRegions()
        }

        await this.loadItems()
      } catch (e) {
        this.countries = []
        this.selectedCountry = null
        this.selectedCountryId = null

        notifyService.fetch_error('country', e)
      }
    },
    async loadRegions() {
      let api

      if (this.selectedCountryId != null) {
        api = `${COUNTRIES_API}/${this.selectedCountryId}/regions`
      } else {
        api = `${REGIONS_API}/list`
      }

      try {
        const res = await this.axios.get(api)
        this.regions = res.data.data
        this.selectedRegion = null

        if (this.selectedRegionId) {
          this.selectedRegion = this.regions.find(
            a => a.id === this.selectedRegionId
          )
          this.selectedRegionId = this.selectedRegion && this.selectedRegion.id
        }

        if (!this.selectedRegion) {
          this.$refs.regionSelector.focus()
        }

        await this.loadItems()
      } catch (e) {
        this.regions = []
        this.selectedRegion = null
        this.selectedRegionId = null

        notifyService.fetch_error('region', e)
      }
    },
    async loadItems() {
      try {
        const api = `${CITIES_API}`
        const params = {
          area_code: this.selectedAreaCode,
          country_id: this.selectedCountryId,
          region_id: this.selectedRegionId,
          search: this.searchName,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('city', e)
      } finally {
        this.loadingItems = false
      }
    },
    updateQuery() {
      const query = {
        ac: this.selectedAreaCode,
        ci: this.selectedCountryId,
        ri: this.selectedRegionId,
        search: this.searchName,
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilter: debounce(function() {
      this.updateQuery()
      this.loadItems()
    }, 500),
    onSelectArea(option) {
      this.selectedArea = option
      this.selectedAreaCode = option ? option.code : null

      this.selectedCountry = null
      this.selectedCountryId = null
      this.selectedRegion = null
      this.selectedRegionId = null

      this.updateQuery()
      this.loadCountries()
    },
    onSelectCountry(option) {
      this.selectedCountry = option
      this.selectedCountryId = option ? option.id : null

      this.selectedRegion = null
      this.selectedRegionId = null

      this.updateQuery()
      this.loadRegions()
    },
    onSelectRegion(option) {
      this.selectedRegion = option
      this.selectedRegionId = option ? option.id : null

      this.updateQuery()
      this.loadItems()
    },
    onPageChange(page) {
      this.page = page

      this.updateQuery()
      this.loadItems()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order

      this.updateQuery()
      this.loadItems()
    },

    onAdd() {
      this.selectedItem = {
        id: null,
        area_code: this.selectedAreaCode,
        country_id: this.selectedCountryId,
        region_id: this.selectedRegionId,
        template_code: null
      }
      this.isCreateModalActive = true
    },
    onModify(item) {
      this.selectedItem = item
      this.isUpdateModalActive = true
    },
    onSend(item) {
      if (this.selectedItem.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        this.sending = true
        await this.axios.post(CITIES_API, item)
        this.isCreateModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${CITIES_API}/${item.id}`

        this.sending = true
        await this.axios.put(api, item)

        notifyService.success(this.$t('attributes.update_succeed'))

        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
